import apiHandler from '@api/ApiHandler.ts';
import {
  Case,
  CreateCaseRequest,
  SimpleCaseForDropdown,
  ExtractedCaseInfo,
} from './types';
import { FILE_SIZE_LIMIT, ALLOWED_FILE_EXTENSIONS } from '@static/staticData';

/**
 * Retry utility with exponential backoff
 * @param fn - Function to retry
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @param initialDelay - Initial delay in milliseconds (default: 1000)
 * @returns Promise that resolves with the function result or rejects after max retries
 */
const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  initialDelay = 1000
): Promise<T> => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error: any) {
      // Don't retry on client errors (4xx) - these are permanent failures
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        throw error;
      }

      // If this is the last retry, throw the error
      if (i === maxRetries - 1) {
        throw error;
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = initialDelay * 2 ** i;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw new Error('Max retries exceeded');
};

/**
 * Validate files before upload
 * @param files - Array of files to validate
 * @returns Object with isValid flag and error message if invalid
 */
const validateFiles = (files: File[]): { isValid: boolean; error?: string } => {
  if (!files || files.length === 0) {
    return { isValid: false, error: 'No files provided' };
  }

  const invalidFiles: string[] = [];

  for (const file of files) {
    // Check file size
    if (file.size > FILE_SIZE_LIMIT) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const limitMB = (FILE_SIZE_LIMIT / (1024 * 1024)).toFixed(0);
      invalidFiles.push(
        `${file.name} (${sizeMB}MB exceeds ${limitMB}MB limit)`
      );
      continue;
    }

    // Check file extension
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !ALLOWED_FILE_EXTENSIONS.includes(fileExtension)) {
      invalidFiles.push(
        `${file.name} (${fileExtension || 'no extension'} || allowed: ${ALLOWED_FILE_EXTENSIONS.join(', ')})`
      );
    }
  }

  if (invalidFiles.length > 0) {
    return {
      isValid: false,
      error: `Invalid files:\n${invalidFiles.join('\n')}`,
    };
  }

  return { isValid: true };
};

/**
 * Calculate timeout based on total file size
 * Base timeout: 5 minutes + 1 minute per 10MB
 * Max timeout: 45 minutes
 */
const calculateUploadTimeout = (files: File[]): number => {
  const totalSizeMB =
    files.reduce((sum, file) => sum + file.size, 0) / (1024 * 1024);
  const baseTimeout = 5 * 60 * 1000; // 5 minutes
  const per10MBTimeout = (totalSizeMB / 10) * 60 * 1000; // 1 minute per 10MB
  const maxTimeout = 45 * 60 * 1000; // 45 minutes max

  const calculatedTimeout = baseTimeout + per10MBTimeout;
  return Math.min(calculatedTimeout, maxTimeout);
};

export const getCasesForDropdown = (
  onSuccess: (data: SimpleCaseForDropdown[]) => void,
  onError?: (error: any) => void
) => {
  apiHandler
    .get<SimpleCaseForDropdown[]>('/cases/dropdown-list/')
    .then((response) => onSuccess(response as any))
    .catch((error) => onError?.(error));
};

export const createCase = (
  data: CreateCaseRequest | FormData,
  onSuccess: (data: Case) => void,
  onError?: (error: any) => void
) => {
  const config =
    data instanceof FormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : undefined;

  apiHandler
    .post<Case>('/cases/', data, config)
    .then((response) => onSuccess(response as any))
    .catch((error) => onError?.(error));
};

export const getCaseById = (
  id: number,
  onSuccess: (data: Case) => void,
  onError?: (error: any) => void
) => {
  apiHandler
    .get<Case>(`/cases/${id}/`)
    .then((response) => onSuccess(response as any))
    .catch((error) => onError?.(error));
};

export const uploadCaseFile = (
  caseId: number,
  formData: FormData,
  onSuccess: (data: any) => void,
  onError?: (error: any) => void
) => {
  apiHandler
    .post(`/cases/${caseId}/case-files/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((response) => onSuccess(response as any))
    .catch((error) => onError?.(error));
};

export const deleteCaseFile = (
  caseId: number,
  fileId: number,
  onSuccess: () => void,
  onError?: (error: any) => void
) => {
  apiHandler
    .delete(`/cases/${caseId}/case-files/${fileId}/`)
    .then(() => onSuccess())
    .catch((error) => onError?.(error));
};

export const uploadMedicalFile = (
  caseId: number,
  formData: FormData,
  onSuccess: (data: any) => void,
  onError?: (error: any) => void
) => {
  apiHandler
    .post(`/cases/${caseId}/medical-files/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((response) => onSuccess(response as any))
    .catch((error) => onError?.(error));
};

export const deleteMedicalFile = (
  caseId: number,
  fileId: number,
  onSuccess: () => void,
  onError?: (error: any) => void
) => {
  apiHandler
    .delete(`/cases/${caseId}/medical-files/${fileId}/`)
    .then(() => onSuccess())
    .catch((error) => onError?.(error));
};

export const uploadDepositionFile = (
  caseId: number,
  formData: FormData,
  onSuccess: (data: any) => void,
  onError?: (error: any) => void
) => {
  apiHandler
    .post(`/cases/${caseId}/deposition-files/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((response) => onSuccess(response as any))
    .catch((error) => onError?.(error));
};

export const deleteDepositionFile = (
  caseId: number,
  fileId: number,
  onSuccess: () => void,
  onError?: (error: any) => void
) => {
  apiHandler
    .delete(`/cases/${caseId}/deposition-files/${fileId}/`)
    .then(() => onSuccess())
    .catch((error) => onError?.(error));
};

export const updateCase = (
  caseId: number,
  data: any,
  onSuccess: (data: Case) => void,
  onError?: (error: any) => void
) => {
  apiHandler
    .put<Case>(`/cases/${caseId}/`, data)
    .then((response) => onSuccess(response as any))
    .catch((error) => onError?.(error));
};

export const patchCase = (
  caseId: number,
  data: any,
  onSuccess: (data: Case) => void,
  onError?: (error: any) => void
) => {
  apiHandler
    .patch<Case>(`/cases/${caseId}/`, data)
    .then((response) => onSuccess(response as any))
    .catch((error) => onError?.(error));
};

export const deleteCase = (
  caseId: number,
  onSuccess: () => void,
  onError?: (error: any) => void
) => {
  apiHandler
    .delete(`/cases/${caseId}/`)
    .then(() => onSuccess())
    .catch((error) => onError?.(error));
};

/**
 * Files sent per request.
 *
 * A request carrying more than DATA_UPLOAD_MAX_NUMBER_FILES is refused by
 * Django while it is still parsing, before any view runs, so the response is a
 * bare 400 with nothing in it to explain why. Attaching 109 medical records in
 * one go failed on exactly that. Records for a personal injury matter arrive in
 * the hundreds, so the answer is to send them in batches rather than to keep
 * raising a ceiling. Smaller requests also fail more cheaply: a dropped
 * connection costs one batch, not the whole upload.
 */
const UPLOAD_CHUNK_SIZE = 20;

const chunk = <T>(items: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
};

const describeUploadError = (error: any, filesInChunk: File[]): string => {
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return 'Upload timed out. Large files may take longer. Please check your connection and try again.';
  }
  // Django refuses an oversized request during parsing, so there is no API
  // message to read. Say something the user can act on instead of passing on
  // an empty 400.
  if (error?.response?.status === 400 && !error?.response?.data) {
    return `The server rejected a batch of ${filesInChunk.length} files. Please try again with fewer files.`;
  }
  if (error?.response?.status === 413) {
    return 'Those files are too large to upload together. Please try again with fewer files.';
  }
  return (
    error?.response?.data?.error?.message ||
    (typeof error?.response?.data?.error === 'string'
      ? error.response.data.error
      : '') ||
    error?.response?.data?.message ||
    error?.message ||
    'An error occurred while uploading files. Please try again.'
  );
};

export type UploadFileState =
  | 'waiting'
  | 'uploading'
  | 'saving'
  | 'done'
  | 'failed';

/** Where one picked file is in its upload (ZLAYT-1514). */
export interface UploadFileStatus {
  name: string;
  state: UploadFileState;
  /** Share of this file's bytes sent, 0 to 100. */
  percent: number;
  reason?: string;
}

export type UploadProgressCallback = (statuses: UploadFileStatus[]) => void;

/** 53-bit string hash (cyrb53), as 14 hex characters. */
const hash53 = (text: string, seed: number): string => {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0))
    .toString(16)
    .padStart(14, '0');
};

/**
 * The key sent with a file, and again on every retry. The server returns the
 * file it already saved for a key it has seen, so a retry after a timeout
 * (when the first request may still have finished) does not save the same
 * file twice (ZLAYT-1514).
 *
 * It comes from the file itself (name, size, last modified), not from a
 * random number, so the same file picked again by hand after it showed
 * Failed carries the same key and is not saved a second time either
 * (ZLAYT-1530). A different file gets a different key; if one ever arrived
 * with a key the case already holds for another file, the server refuses it
 * with a message rather than dropping it.
 */
const uploadKeyFor = (file: File): string => {
  const identity = [file.name, file.size, file.lastModified].join('\u0000');
  return `f1-${hash53(identity, 1)}${hash53(identity, 2)}`;
};

/**
 * Per-file percentages from the bytes sent for a batch: files are sent in
 * order, so a file is complete once the bytes before and including it are.
 */
const batchPercents = (batch: File[], loaded: number, total: number) => {
  const sizes = batch.map((f) => Math.max(1, f.size));
  const sum = sizes.reduce((a, b) => a + b, 0);
  const sent = total > 0 ? (loaded / total) * sum : 0;
  let before = 0;
  return sizes.map((size) => {
    const pct = Math.round(
      (Math.min(Math.max(sent - before, 0), size) / size) * 100
    );
    before += size;
    return pct;
  });
};

/**
 * Upload files to a batch endpoint, a chunk at a time.
 *
 * Reports partial progress honestly: if a later chunk fails, the caller is told
 * how many files did land rather than being left to assume none of them did.
 * onProgress, when given, gets every file's state as it changes.
 */
const uploadFilesInChunks = async (
  endpoint: string,
  files: File[],
  onSuccess: (data: any) => void,
  onError?: (error: any) => void,
  onProgress?: UploadProgressCallback
) => {
  const validation = validateFiles(files);
  if (!validation.isValid) {
    const error = new Error(validation.error);
    onError?.({ ...error, userMessage: validation.error });
    return;
  }

  const keys = files.map(uploadKeyFor);
  const statuses: UploadFileStatus[] = files.map((f) => ({
    name: f.name,
    state: 'waiting',
    percent: 0,
  }));
  const report = () => onProgress?.(statuses.map((s) => ({ ...s })));
  const mark = (from: number, to: number, patch: Partial<UploadFileStatus>) => {
    for (let j = from; j < to; j += 1) Object.assign(statuses[j], patch);
  };
  report();

  const batches = chunk(files, UPLOAD_CHUNK_SIZE);
  const uploaded: any[] = [];
  const rejected: { filename: string; reason: string }[] = [];

  for (let i = 0; i < batches.length; i += 1) {
    const batch = batches[i];
    const start = i * UPLOAD_CHUNK_SIZE;
    const end = start + batch.length;
    const formData = new FormData();
    batch.forEach((file, j) => {
      formData.append('files', file);
      formData.append('upload_keys', keys[start + j]);
    });
    mark(start, end, { state: 'uploading', percent: 0 });
    report();

    try {
      const response = await retryWithBackoff(
        () =>
          apiHandler.post(endpoint, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: calculateUploadTimeout(batch),
            onUploadProgress: (event: { loaded: number; total?: number }) => {
              const percents = batchPercents(
                batch,
                event.loaded,
                event.total || event.loaded
              );
              percents.forEach((pct, j) => {
                const s = statuses[start + j];
                s.percent = pct;
                // All bytes are on the server; it is still storing them.
                s.state = pct >= 100 ? 'saving' : 'uploading';
              });
              report();
            },
          }),
        3,
        2000
      );
      const payload = response as any;
      mark(start, end, { state: 'done', percent: 100 });
      // 207 means the batch took some files and refused others, naming them.
      if (payload && Array.isArray(payload.rejected)) {
        rejected.push(...payload.rejected);
        if (Array.isArray(payload.files)) uploaded.push(...payload.files);
        payload.rejected.forEach((r: { filename: string; reason: string }) => {
          for (let j = start; j < end; j += 1) {
            if (statuses[j].name === r.filename) {
              Object.assign(statuses[j], { state: 'failed', reason: r.reason });
            }
          }
        });
      } else if (Array.isArray(payload)) {
        uploaded.push(...payload);
      } else if (payload) {
        uploaded.push(payload);
      }
      report();
    } catch (error: any) {
      const done = i * UPLOAD_CHUNK_SIZE;
      const detail = describeUploadError(error, batch);
      mark(start, end, { state: 'failed', reason: detail });
      mark(end, files.length, {
        state: 'failed',
        reason: 'Not sent: an earlier batch failed.',
      });
      report();
      onError?.({
        ...error,
        uploadedCount: done,
        totalCount: files.length,
        uploaded,
        userMessage: done
          ? `${detail} ${done} of ${files.length} files were uploaded before this happened.`
          : detail,
      });
      // Hand back what did land. Without this the caller never refreshed, so
      // files that were already saved stayed invisible until the next upload
      // happened to succeed, which is exactly what was reported.
      if (uploaded.length) onSuccess(uploaded);
      return;
    }
  }

  if (rejected.length) {
    const names = rejected
      .map((r) => r.filename)
      .slice(0, 3)
      .join(', ');
    onError?.({
      partial: true,
      uploaded,
      rejected,
      userMessage:
        `${uploaded.length} of ${files.length} files uploaded. ` +
        `Could not accept: ${names}${rejected.length > 3 ? ', and others' : ''}.`,
    });
  }
  onSuccess(uploaded);
};

export const uploadCaseFilesBatch = (
  caseId: number,
  files: File[],
  onSuccess: (data: any) => void,
  onError?: (error: any) => void,
  onProgress?: UploadProgressCallback
) => {
  uploadFilesInChunks(
    `/cases/${caseId}/case-files/batch-upload/`,
    files,
    onSuccess,
    onError,
    onProgress
  );
};

export const uploadMedicalFilesBatch = (
  caseId: number,
  files: File[],
  onSuccess: (data: any) => void,
  onError?: (error: any) => void,
  onProgress?: UploadProgressCallback
) => {
  uploadFilesInChunks(
    `/cases/${caseId}/medical-files/batch-upload/`,
    files,
    onSuccess,
    onError,
    onProgress
  );
};

export const uploadDepositionFilesBatch = (
  caseId: number,
  files: File[],
  onSuccess: (data: any) => void,
  onError?: (error: any) => void,
  onProgress?: UploadProgressCallback
) => {
  // Same path as the other kinds since ZLAYT-1514: batches, retries that are
  // safe to repeat, and a state per file.
  uploadFilesInChunks(
    `/cases/${caseId}/deposition-files/batch-upload/`,
    files,
    onSuccess,
    onError,
    onProgress
  );
};

export const extractCaseInformation = (
  files: File[],
  onSuccess: (data: ExtractedCaseInfo) => void,
  onError?: (error: any) => void
) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  apiHandler
    .post<ExtractedCaseInfo>('/cases/extract-information/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((response) => onSuccess(response as any))
    .catch((error) => onError?.(error));
};

export type RelinkDocumentType =
  | 'demand_letter'
  | 'deposition_summary'
  | 'use_case';

/**
 * Move a document to another case owned by the same user (ZLAYT-1402).
 * Demand letters pass the document version id; the others pass their own id.
 */
export const relinkDocumentToCase = (
  targetCaseId: number,
  documentType: RelinkDocumentType,
  documentId: number,
  onSuccess: (data: { message: string; case_id: number }) => void,
  onError?: (error: any) => void
) => {
  apiHandler
    .post(`/cases/${targetCaseId}/relink-document/`, {
      document_type: documentType,
      document_id: documentId,
    })
    .then((response) => onSuccess(response as any))
    .catch((error) => onError?.(error));
};

/** Pull a readable message out of an API error, whatever shape it has. */
export const apiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data ?? error?.data ?? error;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (typeof data.error === 'string') return data.error;
  if (typeof data.error?.message === 'string') return data.error.message;
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.message === 'string') return data.message;
  const firstField = Object.keys(data).find((k) => Array.isArray(data[k]));
  if (firstField)
    return `${firstField.replace(/_/g, ' ')}: ${data[firstField][0]}`;
  return fallback;
};
